/**
 * techRequest (TECH-REQUEST-EDIT, Seth 2026-10-22: "where do I add or find or
 * adjust that 'request a tech' after the fact on an existing customer?") —
 * the ONE writer for a customer's requested tech, shared by Edit customer
 * details, Book a Call ("★ Save as this customer's tech") and the Watch For
 * editor's roster chips.
 *
 * The request is a confirmed `tech_pref` watch-out in extracted_signal, so
 * the header badge, the Watch For card, Book a Call's crowns, the board and
 * the tech app all read the same row. Setting a new one:
 *   - revives the matching older row when the same name was on file before
 *     (extracted_signal_dedupe is UNIQUE on custno + kind + lower(value) +
 *     property — a plain insert of "Jeremy" after a switched-off "Jeremy"
 *     would fail);
 *   - inserts otherwise;
 *   - switches off every other active request (on file, hidden — the Watch
 *     For editor's "switched off" list brings one back). "Don't send X" rows
 *     are exclusions, not requests, and are left alone.
 * "No preference" = switch the active requests off, write nothing new.
 */
import { supabase } from "@/lib/supabase";
import type { ActiveTech } from "@/features/board/api";
import type { CustomerSignal } from "./api";

export interface TechPick { empno: string; first: string }

/** The roster's first name for a tech ("Jeremy"), falling back to the ESC name. */
export function techFirst(t: ActiveTech): string {
  return (t.firstname ?? t.empname ?? "").trim().split(/\s+/)[0] || t.empname || t.empno;
}

/** "Jeremy Smith" for the picker — first name plus last, never the all-caps ESC empname alone. */
export function techLabel(t: ActiveTech): string {
  return `${techFirst(t)}${t.lastname ? ` ${t.lastname.trim()}` : ""}`;
}

export function isExcludeRequest(s: CustomerSignal): boolean {
  return !!(s.detail && (s.detail as Record<string, unknown>).exclude) || /^(not|no|avoid|don'?t|do not|never)\b/i.test(s.value);
}

/** The active REQUESTS on file (confirmed first, then proposed) — not hidden, not archived, not an exclusion. */
export function activeTechRequests(signals: CustomerSignal[] | undefined): CustomerSignal[] {
  return (signals ?? [])
    .filter((s) => s.signal_type === "tech_pref" && !s.hidden_at && !s.archived_at && (s.status === "confirmed" || s.status === "proposed") && !isExcludeRequest(s))
    .sort((a, b) => (a.status === b.status ? (b.confirmed_at ?? b.created_at ?? "").localeCompare(a.confirmed_at ?? a.created_at ?? "") : a.status === "confirmed" ? -1 : 1));
}

/** Which roster techs a request names — detail.empnos when the writer stored them, else every roster first name found as a whole word ("Jeremy or Camron" → both). */
export function rosterTechsFor(s: CustomerSignal, roster: ActiveTech[]): ActiveTech[] {
  const empnos = (s.detail as { empnos?: unknown } | null)?.empnos;
  if (Array.isArray(empnos) && empnos.length > 0) {
    const byNo = empnos.map((e) => roster.find((t) => t.empno === String(e))).filter((t): t is ActiveTech => !!t);
    if (byNo.length > 0) return byNo;
  }
  const hay = ` ${s.value.toLowerCase()} `;
  const found: ActiveTech[] = [];
  for (const t of roster) {
    const first = techFirst(t).toLowerCase();
    if (first && new RegExp(`\\b${first.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(hay) && !found.includes(t)) found.push(t);
  }
  return found;
}

/** The customer's current confirmed request resolved to roster techs (an unconfirmed note-read never counts as the setting). */
export function currentTechRequest(signals: CustomerSignal[] | undefined, roster: ActiveTech[]): { row: CustomerSignal; techs: ActiveTech[] } | null {
  const row = activeTechRequests(signals).find((s) => s.status === "confirmed");
  return row ? { row, techs: rosterTechsFor(row, roster) } : null;
}

/**
 * Set the customer's requested tech(s). `picks` empty = no preference.
 * Writes extracted_signal only; the caller invalidates ["customer_signals", custno].
 */
export async function setTechRequest(args: {
  custno: string;
  picks: TechPick[];
  signals: CustomerSignal[] | undefined;
  /** who wrote it — "edit_customer" | "schedule" | "watch_outs" */
  source: string;
  /** property scope for a NEW row; revived rows keep theirs */
  locno?: string | null;
}): Promise<void> {
  const { custno, picks, signals, source } = args;
  const now = new Date().toISOString();
  const who = (await supabase.auth.getUser()).data.user?.email ?? null;
  const active = activeTechRequests(signals);
  const hideRows = async (rows: CustomerSignal[]) => {
    if (rows.length === 0) return;
    const { error } = await supabase.from("extracted_signal")
      .update({ hidden_at: now, hidden_by: who, updated_at: now })
      .in("id", rows.map((r) => r.id));
    if (error) throw error;
  };

  if (picks.length === 0) { await hideRows(active); return; }

  const value = picks.map((p) => p.first).join(" or ");
  const empnos = picks.map((p) => p.empno);
  const detail = { source, empnos };
  // Same name already on file (switched off, dismissed, or still showing)? Revive that row — the dedupe index owns the name.
  const same = (signals ?? []).find((s) => s.signal_type === "tech_pref" && !s.archived_at && s.value.trim().toLowerCase() === value.toLowerCase());
  if (same) {
    const { error } = await supabase.from("extracted_signal").update({
      status: "confirmed", hidden_at: null, hidden_by: null, detail,
      confirmed_by: who, confirmed_at: now, updated_at: now,
    }).eq("id", same.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("extracted_signal").insert({
      custno, locno: args.locno ?? null, signal_type: "tech_pref", value, detail,
      source: "manual", status: "confirmed", confidence: 1,
      confirmed_by: who, confirmed_at: now, created_at: now, updated_at: now,
    });
    if (error) throw error;
  }
  await hideRows(active.filter((s) => s.id !== same?.id));
}
